import React, { useState, useEffect, useCallback } from 'react';
import { Category, CreateCategory, UpdateCategory } from '../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { apiService } from '../services/api';
import SearchBar from './SearchBar';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';

interface CategoriesTabProps {
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const CategoriesTab: React.FC<CategoriesTabProps> = ({ onError, onSuccess, onRegisterCreateAction }) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreate = hasPermission(user, permissions.masterData.create);
  const canEdit = hasPermission(user, permissions.masterData.edit);
  const canDelete = hasPermission(user, permissions.masterData.delete);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState<CreateCategory>({
    categoryCode: '',
    categoryName: '',
    description: '',
    displayOrder: undefined,
    isActive: true,
  });

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiService.getAllCategories(includeInactive);
      // Filter by search term client-side
      let filtered = result;
      if (searchTerm) {
        filtered = result.filter(c => 
          c.categoryCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (c.description && c.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      setCategories(filtered);
    } catch (err: any) {
      onError('Failed to fetch categories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onError, includeInactive, searchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        categoryCode: category.categoryCode,
        categoryName: category.categoryName,
        description: category.description || '',
        displayOrder: category.displayOrder || undefined,
        isActive: category.isActive,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        categoryCode: '',
        categoryName: '',
        description: '',
        displayOrder: undefined,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({
      categoryCode: '',
      categoryName: '',
      description: '',
      displayOrder: undefined,
      isActive: true,
    });
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      if (editingCategory) {
        if (!canEdit) {
          onError('You do not have permission to edit categories.');
          return;
        }
        const updateData: UpdateCategory = {
          categoryCode: formData.categoryCode,
          categoryName: formData.categoryName,
          description: formData.description || undefined,
          displayOrder: formData.displayOrder,
          isActive: formData.isActive,
        };
        await apiService.updateCategory(editingCategory.categoryId, updateData);
        onSuccess('Category updated successfully!');
      } else {
        if (!canCreate) {
          onError('You do not have permission to create categories.');
          return;
        }
        await apiService.createCategory(formData);
        onSuccess('Category created successfully!');
      }

      handleCloseDialog();
      fetchCategories();
    } catch (err: any) {
      onError(err.response?.data || 'Operation failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!canDelete) {
      onError('You do not have permission to delete categories.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Are you sure you want to delete this category?' });
    if (!confirmed) return;
    try {
      await apiService.deleteCategory(id);
      onSuccess('Category deleted successfully!');
      fetchCategories();
    } catch (err: any) {
      onError(err.response?.data || 'Failed to delete category.');
    }
  };

  useEffect(() => {
    onRegisterCreateAction?.(canCreate ? () => handleOpenDialog() : null);
    return () => onRegisterCreateAction?.(null);
  }, [onRegisterCreateAction, canCreate]);

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'text-green-300 bg-green-900/50'
      : 'text-red-300 bg-red-900/50';
  };

  const getStatusDot = (isActive: boolean) => {
    return isActive ? 'bg-green-500' : 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ListPageRoot>
      <ListPageToolbar className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:flex-1 sm:min-w-0">
          <SearchBar
            variant="listPage"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={setSearchTerm}
            className="w-full sm:max-w-md"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: appColors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: appColors.primary,
                  },
                }}
              />
            }
            label="Include Inactive"
            sx={{ color: 'white' }}
          />
        </div>
      </ListPageToolbar>

      <ListPageContentSection>
        <ListPageTableSurface>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <ListPageTableHeadRow>
                  <ListPageTh>ID</ListPageTh>
                  <ListPageTh>Code</ListPageTh>
                  <ListPageTh>Name</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Description</ListPageTh>
                  <ListPageTh className="text-center">Display Order</ListPageTh>
                  <ListPageTh className="text-center">Status</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Created</ListPageTh>
                  <ListPageTh className="text-right">Actions</ListPageTh>
                </ListPageTableHeadRow>
              </thead>
              <tbody className={listPageTableBodyClass}>
                {categories.map((category) => (
                  <tr key={category.categoryId} className={listPageTableRowClass}>
                    <td className="px-5 py-4 text-slate-300">#{category.categoryId}</td>
                    <td className="px-5 py-4 text-white font-medium">{category.categoryCode}</td>
                    <td className="px-5 py-4 text-white font-medium">{category.categoryName}</td>
                    <td className="px-5 py-4 text-slate-400 hidden lg:table-cell">{category.description || '—'}</td>
                    <td className="px-5 py-4 text-center text-slate-300">{category.displayOrder ?? '—'}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(category.isActive)}`}>
                        <span className={`w-2 h-2 mr-2 rounded-full ${getStatusDot(category.isActive)}`} />
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300 hidden lg:table-cell">
                      {category.createdAt ? new Date(category.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="action-row-icon-buttons">
                        {canEdit && (
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            aria-label="Edit category"
                            onClick={() => handleOpenDialog(category)}
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="text-red-400 hover:bg-red-500/10"
                            aria-label="Delete category"
                            onClick={() => handleDelete(category.categoryId)}
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListPageTableSurface>

        <ListPageMobileStack>
          {categories.map((category) => (
            <div
              key={category.categoryId}
              className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="material-symbols-outlined text-base text-primary shrink-0">category</span>
                    <h3 className="text-white font-semibold text-lg">{category.categoryName}</h3>
                    <span className="text-xs text-slate-500">#{category.categoryId}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-1">Code: {category.categoryCode}</p>
                  {category.description && <p className="text-slate-300 text-sm">{category.description}</p>}
                </div>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(category.isActive)}`}>
                  <span className={`w-2 h-2 mr-1 rounded-full ${getStatusDot(category.isActive)}`} />
                  {category.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                {category.displayOrder != null && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm text-primary">sort</span>
                    Order: {category.displayOrder}
                  </span>
                )}
                {category.createdAt && <span>{new Date(category.createdAt).toLocaleDateString()}</span>}
              </div>

              <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                {canEdit && (
                  <button
                    type="button"
                    className="text-primary hover:bg-primary/10"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDelete(category.categoryId)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </ListPageMobileStack>
      </ListPageContentSection>

      {/* Category Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm" 
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          {editingCategory ? `Edit Category - ${editingCategory.categoryName}` : 'Create New Category'}
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: '20px' }}>
          <div className="space-y-4">
            <TextField
              autoFocus
              margin="dense"
              label="Category Code"
              fullWidth
              variant="outlined"
              value={formData.categoryCode}
              onChange={(e) => setFormData({ ...formData, categoryCode: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Category Name"
              fullWidth
              variant="outlined"
              value={formData.categoryName}
              onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Display Order"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.displayOrder || ''}
              onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value ? parseInt(e.target.value) : undefined })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: appColors.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: appColors.primary,
                    },
                  }}
                />
              }
              label="Active"
              sx={{ color: 'white' }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              color: appColors.mutedText, 
              borderColor: appColors.dialogBorder,
              '&:hover': {
                borderColor: appColors.primary,
                color: appColors.primary,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: appColors.primary,
              '&:hover': {
                backgroundColor: appColors.primary,
                opacity: 0.9,
              }
            }}
          >
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default CategoriesTab;

